import React from 'react';
import { motion } from 'framer-motion';
export interface SentencePart {
  /** The word or phrase */
  text: string;
  /** Grammatical role (e.g., 'onderwerp', 'gezegde', 'lijdend voorwerp') */
  role: string;
  /** Optional abbreviation for compact display */
  abbr?: string;
}
export interface SentenceDiagramProps {
  /** The full sentence */
  sentence: string;
  /** Parsed parts with roles */
  parts: SentencePart[];
  /** Optional label */
  label?: string;
  /** 'stimulus' = compact, 'support' = educational panel */
  mode?: 'stimulus' | 'support';
}
/** Color mapping for Dutch grammatical roles — aligned with Taalverhaal method */
const ROLE_COLORS: Record<string, {
  bg: string;
  border: string;
  text: string;
  underline: string;
}> = {
  onderwerp: {
    bg: 'bg-red-100',
    border: 'border-red-300',
    text: 'text-red-800',
    underline: 'border-red-400'
  },
  gezegde: {
    bg: 'bg-blue-100',
    border: 'border-blue-300',
    text: 'text-blue-800',
    underline: 'border-blue-400'
  },
  'werkwoordelijk gezegde': {
    bg: 'bg-blue-100',
    border: 'border-blue-300',
    text: 'text-blue-800',
    underline: 'border-blue-400'
  },
  'naamwoordelijk gezegde': {
    bg: 'bg-sky-100',
    border: 'border-sky-300',
    text: 'text-sky-800',
    underline: 'border-sky-400'
  },
  'lijdend voorwerp': {
    bg: 'bg-green-100',
    border: 'border-green-300',
    text: 'text-green-800',
    underline: 'border-green-400'
  },
  'meewerkend voorwerp': {
    bg: 'bg-amber-100',
    border: 'border-amber-300',
    text: 'text-amber-800',
    underline: 'border-amber-400'
  },
  'bijwoordelijke bepaling': {
    bg: 'bg-purple-100',
    border: 'border-purple-300',
    text: 'text-purple-800',
    underline: 'border-purple-400'
  },
  'bepaling van tijd': {
    bg: 'bg-purple-100',
    border: 'border-purple-300',
    text: 'text-purple-800',
    underline: 'border-purple-400'
  },
  'bepaling van plaats': {
    bg: 'bg-violet-100',
    border: 'border-violet-300',
    text: 'text-violet-800',
    underline: 'border-violet-400'
  },
  'bepaling van wijze': {
    bg: 'bg-fuchsia-100',
    border: 'border-fuchsia-300',
    text: 'text-fuchsia-800',
    underline: 'border-fuchsia-400'
  },
  voorzetselvoorwerp: {
    bg: 'bg-teal-100',
    border: 'border-teal-300',
    text: 'text-teal-800',
    underline: 'border-teal-400'
  },
  bijzin: {
    bg: 'bg-orange-100',
    border: 'border-orange-300',
    text: 'text-orange-800',
    underline: 'border-orange-400'
  },
  voegwoord: {
    bg: 'bg-slate-100',
    border: 'border-slate-300',
    text: 'text-slate-700',
    underline: 'border-slate-400'
  }
};
const DEFAULT_COLOR = {
  bg: 'bg-gray-100',
  border: 'border-gray-300',
  text: 'text-gray-700',
  underline: 'border-gray-400'
};
/** Standard abbreviations for Dutch grammatical roles */
const ROLE_ABBR: Record<string, string> = {
  onderwerp: 'OW',
  gezegde: 'GZ',
  'werkwoordelijk gezegde': 'WG',
  'naamwoordelijk gezegde': 'NG',
  'lijdend voorwerp': 'LV',
  'meewerkend voorwerp': 'MV',
  'bijwoordelijke bepaling': 'BwB',
  'bepaling van tijd': 'BvT',
  'bepaling van plaats': 'BvP',
  'bepaling van wijze': 'BvW',
  voorzetselvoorwerp: 'VzV',
  bijzin: 'BZ',
  voegwoord: 'VW'
};
function getColor(role: string) {
  const key = role.toLowerCase();
  return ROLE_COLORS[key] ?? DEFAULT_COLOR;
}
function getAbbr(role: string, customAbbr?: string): string {
  if (customAbbr) return customAbbr;
  return ROLE_ABBR[role.toLowerCase()] ?? role.slice(0, 3).toUpperCase();
}
export function SentenceDiagram({
  sentence,
  parts,
  label,
  mode = 'support'
}: SentenceDiagramProps) {
  if (!parts || parts.length === 0) {
    return <div className="bg-red-50 p-4 rounded-xl border border-red-200 text-center" data-id="element-10020">
        <p className="text-red-600 text-sm font-medium" data-id="element-10021">
          Geen zinsontleding beschikbaar
        </p>
      </div>;
  }
  // Stimulus mode: compact colored blocks
  if (mode === 'stimulus') {
    return <div className="bg-slate-50 rounded-xl px-5 py-4 border border-slate-200" aria-label={label ?? 'Zinsontleding'} data-id="element-10022">
        {label && <p className="text-sm font-semibold text-slate-800 mb-3 text-center" data-id="element-10023">
            {label}
          </p>}
        <div className="flex flex-wrap items-end gap-1.5" data-id="element-10024">
          {parts.map((part, i) => {
          const c = getColor(part.role);
          return <motion.div key={i} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.08,
            duration: 0.25
          }} className="flex flex-col items-center" data-id="element-10025">
                <span className={`${c.bg} ${c.border} border rounded-md px-2 py-1 text-sm font-medium ${c.text}`} data-id="element-10026">
                  {part.text}
                </span>
                <span className={`text-[10px] font-bold mt-0.5 ${c.text} opacity-70`} data-id="element-10027">
                  {getAbbr(part.role, part.abbr)}
                </span>
              </motion.div>;
        })}
        </div>
      </div>;
  }
  // Support mode: educational panel
  const uniqueRoles = [...new Set(parts.map(p => p.role.toLowerCase()))];
  return <div className="bg-gradient-to-br from-slate-50 to-blue-50 p-6 rounded-2xl border-2 border-slate-200 shadow-sm" aria-label={label ?? 'Zinsontleding'} data-id="element-10028">
      {label && <div className="mb-5 text-center" data-id="element-10029">
          <div className="inline-flex items-center gap-2 bg-white/90 rounded-xl px-4 py-2 border border-slate-200 shadow-sm" data-id="element-10030">
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" data-id="element-10031" />
            <span className="text-sm font-bold text-slate-900" data-id="element-10032">{label}</span>
          </div>
        </div>}

      {/* Full sentence */}
      <div className="bg-white/80 rounded-xl p-4 border border-slate-100 mb-4" data-id="element-10033">
        <p className="text-sm text-slate-500 font-medium mb-2" data-id="element-10034">Zin:</p>
        <p className="text-base text-slate-900 font-medium italic" data-id="element-10035">
          "{sentence}"
        </p>
      </div>

      {/* Colored diagram */}
      <div className="bg-white/80 rounded-xl p-4 border border-slate-100 mb-4" data-id="element-10036">
        <p className="text-sm text-slate-500 font-medium mb-3" data-id="element-10037">Ontleding:</p>
        <div className="flex flex-wrap items-end gap-2" data-id="element-10038">
          {parts.map((part, i) => {
          const c = getColor(part.role);
          return <motion.div key={i} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.15 + i * 0.1,
            duration: 0.3
          }} className="flex flex-col items-center" data-id="element-10039">
                <div className={`${c.bg} ${c.border} border-2 rounded-lg px-3 py-2 font-semibold ${c.text} text-sm border-b-4 ${c.underline}`} data-id="element-10040">
                  {part.text}
                </div>
                <div className={`mt-1 text-xs font-bold ${c.text}`} data-id="element-10041">
                  {getAbbr(part.role, part.abbr)}
                </div>
                <div className={`text-[10px] ${c.text} opacity-60 max-w-[100px] text-center leading-tight`} data-id="element-10042">
                  {part.role}
                </div>
              </motion.div>;
        })}
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white/80 rounded-xl p-4 border border-slate-100 shadow-sm" data-id="element-10043">
        <div className="flex items-start gap-3" data-id="element-10044">
          <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-10045">
            <span className="text-blue-600 text-sm" data-id="element-10046">🏷️</span>
          </div>
          <div data-id="element-10047">
            <h4 className="text-sm font-bold text-slate-900 mb-2" data-id="element-10048">Legenda</h4>
            <div className="flex flex-wrap gap-2" data-id="element-10049">
              {uniqueRoles.map(role => {
              const c = getColor(role);
              return <div key={role} className="flex items-center gap-1.5" data-id="element-10050">
                    <div className={`w-3 h-3 rounded ${c.bg} ${c.border} border`} data-id="element-10051" />
                    <span className="text-xs text-slate-600" data-id="element-10052">
                      <strong data-id="element-10053">
                        {ROLE_ABBR[role] ?? role.slice(0, 3).toUpperCase()}
                      </strong>{' '}
                      = {role}
                    </span>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </div>;
}